import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Inbox } from 'lucide-react';
import { api } from '../../lib/api';
import { useT, type TranslationKey } from '../../i18n';
import { formatMoneyShort, formatNumber } from '../../lib/reportFormat';
import { ageInDays } from '../../lib/format';
import {
  inquiryAmount,
  inquiryRef,
  type DrillInGroup,
  type Inquiry,
  type ProductType,
  type Stage,
  type StageAnalytics,
  type StageBreakdownItem,
  STAGE_ORDER,
} from '../../types/api';
import { ConfidenceBadge } from '../ConfidenceBadge';
import { EmptyState } from '../EmptyState';
import { Modal } from '../Modal';
import { Skeleton } from '../Skeleton';
import { stageLabelKey } from '../../lib/stageLabels';
import styles from './StageDrillIn.module.css';

/** Tabs of the deal list — groups of GET /api/reports/pipeline/group. */
const TABS: { group: DrillInGroup; labelKey: TranslationKey }[] = [
  { group: 'current', labelKey: 'pipeline.sdTabCurrent' },
  { group: 'in', labelKey: 'pipeline.sdTabIn' },
  { group: 'out', labelKey: 'pipeline.sdTabOut' },
  { group: 'dropped', labelKey: 'pipeline.sdTabDropped' },
  { group: 'paused', labelKey: 'pipeline.sdTabPaused' },
];

/** Pseudo-keys emitted by the stage endpoint next to real stage names. */
const PSEUDO_LABEL_KEY: Record<string, TranslationKey> = {
  NEW: 'pipeline.sdFromNew',
  PAUSE: 'pipeline.sdFromPause',
  DROPPED: 'pipeline.sdToDropped',
  PAUSED: 'pipeline.sdToPaused',
  WON: 'pipeline.sdToWon',
};

export interface StageDrillInProps {
  open: boolean;
  stage: Stage | null;
  /** ISO date bounds of the dashboard period. */
  from: string;
  to: string;
  /** Selected Dashboard tab (#75) — stage labels follow the route wording. */
  productType?: ProductType | null;
  onClose: () => void;
}

function breakdownLabel(
  key: string,
  t: (k: TranslationKey) => string,
  productType?: ProductType | null,
): string {
  const pseudo = PSEUDO_LABEL_KEY[key];
  if (pseudo) return t(pseudo);
  if ((STAGE_ORDER as string[]).includes(key)) {
    return t(stageLabelKey(key as Stage, productType));
  }
  return key;
}

function BreakdownList({
  title,
  items,
  emptyText,
  productType,
}: {
  title: string;
  items: StageBreakdownItem[];
  emptyText: string;
  productType?: ProductType | null;
}) {
  const { t, locale } = useT();
  const total = items.reduce((n, b) => n + b.count, 0);
  return (
    <div className={styles.breakdown}>
      <h3 className={styles.breakdownTitle}>{title}</h3>
      {total === 0 ? (
        <p className={styles.breakdownEmpty}>{emptyText}</p>
      ) : (
        <ul className={styles.breakdownList}>
          {items.map((b) => {
            const share = Math.round((b.count / total) * 100);
            return (
              <li className={styles.breakdownRow} key={b.key}>
                <span className={styles.breakdownLabel}>{breakdownLabel(b.key, t, productType)}</span>
                <span className={styles.breakdownBar} aria-hidden>
                  <i style={{ width: `${share}%` }} />
                </span>
                <span className={styles.breakdownCount}>{formatNumber(b.count, locale)}</span>
                <span className={styles.breakdownShare}>{share}%</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Stage analytics drill-in: opens from a funnel stage card. Summary of the
 * stage for the dashboard period (in/out decomposition, snapshot, conversion),
 * transition direction breakdowns, and the deal list per group. A deal row
 * opens the inquiry card via the /inquiries?open=<id> deep link (issue #32).
 */
export function StageDrillIn({ open, stage, from, to, productType, onClose }: StageDrillInProps) {
  const { t, locale } = useT();
  const navigate = useNavigate();
  const [tab, setTab] = useState<DrillInGroup>('current');

  const analyticsQ = useQuery<StageAnalytics>({
    queryKey: ['reports', 'stage', stage, from, to],
    queryFn: () =>
      api.get<StageAnalytics>(`/reports/stage/${stage}`, { query: { from, to } }),
    enabled: open && stage !== null,
  });

  const dealsQ = useQuery<Inquiry[]>({
    queryKey: ['reports', 'pipeline', 'stage-group', stage, tab, from, to],
    queryFn: () =>
      api.get<Inquiry[]>('/reports/pipeline/group', {
        query: { from, to, stage: stage ?? undefined, group: tab },
      }),
    enabled: open && stage !== null,
  });

  if (!stage) return null;
  const summary = analyticsQ.data?.summary;

  const openDeal = (id: string) => {
    onClose();
    navigate(`/inquiries?open=${id}`);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={t(stageLabelKey(stage, productType))}
      description={`${from} — ${to}`}
    >
      {/* ── Summary ─────────────────────────────────────────── */}
      <section aria-label={t('pipeline.sdSummary')}>
        {analyticsQ.isPending && (
          <div className={styles.summaryGrid}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="block" height={64} radius="lg" />
            ))}
          </div>
        )}
        {analyticsQ.isError && (
          <EmptyState
            compact
            icon={<Inbox size={22} />}
            title={t('pipeline.errorTitle')}
            description={analyticsQ.error.message}
          />
        )}
        {summary && (
          <>
            <div className={styles.summaryGrid}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>{t('pipeline.sdCameIn')}</span>
                <b className={styles.statValue}>{formatNumber(summary.in, locale)}</b>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>{t('pipeline.sdWentOut')}</span>
                <b className={styles.statValue}>{formatNumber(summary.out, locale)}</b>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>{t('pipeline.sdCurrent')}</span>
                <b className={styles.statValue}>{formatNumber(summary.current, locale)}</b>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>{t('pipeline.sdCurrentSum')}</span>
                <b className={styles.statValue}>
                  {formatMoneyShort(summary.currentSum, locale, t('pipeline.millionShort'))}
                </b>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>{t('pipeline.sdConversion')}</span>
                <b className={styles.statValue}>
                  {summary.conversionToNext === null
                    ? '—'
                    : `${Math.round(summary.conversionToNext * 100)}%`}
                </b>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>{t('pipeline.sdAvgAge')}</span>
                <b className={styles.statValue}>
                  {summary.avgAgeDays === null
                    ? '—'
                    : formatNumber(summary.avgAgeDays, locale, 1)}
                </b>
              </div>
            </div>
            <p className={styles.outSplit}>
              {t('pipeline.sdOutSplit', {
                advanced: formatNumber(summary.advanced, locale),
                dropped: formatNumber(summary.dropped, locale),
                paused: formatNumber(summary.paused, locale),
                returned: formatNumber(summary.returned, locale),
              })}
            </p>

            <div className={styles.breakdowns}>
              <BreakdownList
                title={t('pipeline.sdFromTitle')}
                items={analyticsQ.data?.breakdownIn ?? []}
                emptyText={t('pipeline.sdEmpty')}
                productType={productType}
              />
              <BreakdownList
                title={t('pipeline.sdToTitle')}
                items={analyticsQ.data?.breakdownOut ?? []}
                emptyText={t('pipeline.sdEmpty')}
                productType={productType}
              />
            </div>
          </>
        )}
      </section>

      {/* ── Deal list ───────────────────────────────────────── */}
      <section>
        <div className={styles.tabs} role="tablist">
          {TABS.map(({ group, labelKey }) => (
            <button
              key={group}
              type="button"
              role="tab"
              aria-selected={tab === group}
              className={clsx(styles.tab, tab === group && styles.tabActive)}
              onClick={() => setTab(group)}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>

        {dealsQ.isPending && (
          <div className={styles.skeletons}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="block" height={48} radius="lg" />
            ))}
          </div>
        )}
        {dealsQ.isError && (
          <EmptyState
            compact
            icon={<Inbox size={22} />}
            title={t('pipeline.errorTitle')}
            description={dealsQ.error.message}
          />
        )}
        {dealsQ.isSuccess && dealsQ.data.length === 0 && (
          <EmptyState compact icon={<Inbox size={22} />} title={t('pipeline.drillInEmpty')} />
        )}
        {dealsQ.isSuccess && dealsQ.data.length > 0 && (
          <ul className={styles.deals}>
            {dealsQ.data.map((d) => {
              const amount = inquiryAmount(d);
              const manager = d.assignedManager?.name ?? d.createdBy?.name;
              return (
                <li key={d.id}>
                  <button type="button" className={styles.deal} onClick={() => openDeal(d.id)}>
                    <span className={styles.dealMain}>
                      <span className={styles.dealClient}>
                        {d.client?.companyName ?? inquiryRef(d)}
                      </span>
                      <span className={styles.dealWhat}>{d.summary}</span>
                    </span>
                    <span className={styles.dealMeta}>
                      <ConfidenceBadge confidence={d.confidence} />
                      <span className={styles.dealManager}>
                        {manager ?? t('pipeline.sdManagerUnset')}
                      </span>
                      <span className={styles.dealAge}>
                        {t('pipeline.sdAgeDays', { days: ageInDays(d.createdAt) })}
                      </span>
                    </span>
                    <span className={styles.dealSum}>
                      {amount !== null && amount > 0
                        ? formatMoneyShort(amount, locale, t('pipeline.millionShort'))
                        : t('pipeline.drillInNoAmount')}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </Modal>
  );
}
